(() => {
  const instances = new Map();
  const modulo = (value, size) => ((value % size) + size) % size;
  class Carousel {
    constructor(element) {
      this.element = element;
      this.track = element.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.items = Array.from(this.track.children);
      this.prev = element.querySelector('[data-carousel-prev]');
      this.next = element.querySelector('[data-carousel-next]');
      this.toggle = element.querySelector('[data-carousel-toggle]');
      this.media = matchMedia('(prefers-reduced-motion: reduce)');
      this.enabled = element.dataset.autoplay === 'true' && !!this.toggle;
      this.manual = !this.enabled;
      this.speed = Math.max(20, Math.min(140, Number(element.dataset.carouselSpeed) || 90));
      this.dragging = false;
      this.focused = false;
      this.visible = false;
      this.period = 0;
      this.position = 0;
      this.clones = [];
      this.frame = 0;
      this.lastTime = null;
      this.settleUntil = 0;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.tick = this.tick.bind(this);
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.position = this.track.scrollLeft;
        this.updateControls();
      }, { passive: true, signal });
      this.prev?.addEventListener('click', () => this.advance(-1), { signal });
      this.next?.addEventListener('click', () => this.advance(1), { signal });
      this.toggle?.addEventListener('click', event => {
        this.manual = !this.manual;
        if (!this.manual && event.detail > 0) this.toggle.blur();
        this.sync();
      }, { signal });
      element.addEventListener('focusin', event => {
        this.focused = event.target.matches(':focus-visible');
        this.sync();
      }, { signal });
      element.addEventListener('focusout', () => queueMicrotask(() => {
        this.focused = element.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        this.sync();
      }), { signal });
      this.track.addEventListener('pointerdown', () => {
        this.dragging = true;
        this.focused = false;
        this.sync();
      }, { passive: true, signal });
      const release = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.settleUntil = performance.now() + 900;
        this.sync();
      };
      document.addEventListener('pointerup', release, { signal });
      document.addEventListener('pointercancel', release, { signal });
      this.track.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        this.settleUntil = performance.now() + 900;
        this.sync();
      }, { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          this.focused = true;
          this.sync();
        }
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.dragging = false;
        this.sync();
      }, { signal });
      this.media.addEventListener('change', () => this.layout(), { signal });
      this.resize = new ResizeObserver(() => this.layout());
      this.resize.observe(this.track);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.01 });
      this.intersection.observe(this.track);
      this.layout();
    }
    stop() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.resumeTimer);
      this.frame = 0;
      this.lastTime = null;
    }
    clone(item) {
      const clone = item.cloneNode(true);
      clone.setAttribute('data-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
      this.clones.push(clone);
      return clone;
    }
    layout() {
      this.stop();
      const offset = this.period ? modulo(this.track.scrollLeft - this.period, this.period) : this.track.scrollLeft;
      this.clones.forEach(clone => clone.remove());
      this.clones = [];
      this.period = 0;
      this.track.classList.remove('tms-carousel-track--continuous');
      this.overflow = this.track.scrollWidth > this.track.clientWidth + 4;
      if (this.enabled && !this.media.matches && this.overflow && this.items.length > 1) {
        this.track.classList.add('tms-carousel-track--continuous');
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.items.forEach(item => before.append(this.clone(item)));
        this.items.forEach(item => after.append(this.clone(item)));
        this.track.prepend(before);
        this.track.append(after);
        this.period = this.items[0].getBoundingClientRect().left - this.clones[0].getBoundingClientRect().left;
        this.position = this.period + modulo(offset, this.period);
      } else this.position = Math.min(offset, this.track.scrollWidth - this.track.clientWidth);
      this.writePosition();
      this.updateControls();
      this.sync();
    }
    writePosition() {
      this.track.scrollLeft = this.position;
      this.writtenPosition = this.track.scrollLeft;
    }
    normalize() {
      if (this.period) this.position = this.period + modulo(this.position - this.period, this.period);
    }
    tick(time) {
      if (Math.abs(this.track.scrollLeft - this.writtenPosition) > 1) this.position = this.track.scrollLeft;
      const delta = this.lastTime === null ? 0 : Math.min(time - this.lastTime, 50);
      this.lastTime = time;
      this.position += this.speed * delta / 1000;
      this.normalize();
      this.writePosition();
      this.frame = requestAnimationFrame(this.tick);
    }
    advance(direction) {
      this.stop();
      this.position = this.track.scrollLeft;
      this.normalize();
      this.writePosition();
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = (this.items[0]?.getBoundingClientRect().width || 300) + gap;
      this.track.scrollTo({ left: this.position + direction * step, behavior: this.media.matches ? 'instant' : 'smooth' });
      this.settleUntil = performance.now() + 900;
      this.sync();
    }
    updateControls() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.disabled = !this.period && this.track.scrollLeft <= 4;
      if (this.next) this.next.disabled = !this.period && this.track.scrollLeft >= max - 4;
      if (this.toggle) this.toggle.hidden = !this.overflow || this.media.matches;
    }
    sync() {
      this.stop();
      if (this.toggle) {
        const label = this.manual ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
        this.toggle.setAttribute('aria-label', label);
        this.toggle.title = label;
        const icon = this.toggle.querySelector('[data-carousel-toggle-icon]');
        if (icon) icon.textContent = this.manual ? '▶' : 'Ⅱ';
      }
      if (this.manual || this.dragging || this.focused || !this.visible || document.hidden || !this.period || this.media.matches) return;
      const remaining = this.settleUntil - performance.now();
      if (remaining > 0) {
        this.resumeTimer = setTimeout(() => this.sync(), remaining);
        return;
      }
      this.position = this.track.scrollLeft;
      this.normalize();
      this.writePosition();
      this.frame = requestAnimationFrame(this.tick);
    }
    destroy() {
      this.stop();
      this.abort.abort();
      this.resize.disconnect();
      this.intersection.disconnect();
      const offset = this.period ? modulo(this.track.scrollLeft - this.period, this.period) : this.track.scrollLeft;
      this.clones.forEach(clone => clone.remove());
      this.track.classList.remove('tms-carousel-track--continuous');
      this.track.scrollLeft = offset;
    }
  }
  const mount = root => root.querySelectorAll('[data-tms-carousel]').forEach(element => {
    if (!instances.has(element)) instances.set(element, new Carousel(element));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [element, carousel] of instances) if (event.target.contains(element)) {
      carousel.destroy();
      instances.delete(element);
    }
  });
})();
